import { useState } from 'react';
import {
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Stack,
} from '@mui/material';
import { useUser } from '../../hooks/useUser';
import {
  PreferencesContainer,
  FormContainer,
} from './ProfilePreferences.styled';
import { User } from '../../types/user';
import CustomTabs from '../Profile/components/CustomTabs';
import CustomTab from '../Profile/components/CustomTab';
import { Person, Settings } from '@mui/icons-material';
import axios from '../../common/api/request';

export function ProfilePreferences() {
  const { isLoading, error, data: user, refreshUserData } = useUser();
  const [activeTab, setActiveTab] = useState(0);
  const [welcomeModule, setWelcomeModule] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize welcome module when user data is loaded

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">Failed to load user data</Alert>;
  }

  if (!user) {
    return null;
  }

  const handleTabChange = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const handleWelcomeModuleChange = (event: any) => {
    const newValue = event.target.value;
    setWelcomeModule(newValue);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post('/user', { ...user, welcomeModule });
      // Invalidate user cache to refresh data after mutation
      await refreshUserData();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save user preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setIsChangingPassword(true);
    try {
      await axios.post('/user/change-password');
      // Handle successful password change - you might want to show a success message
    } catch (error) {
      console.error('Failed to change password:', error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const buttonStyle = {
    display: 'flex',
    height: '30px',
    padding: '0px 24px',
    alignItems: 'center',
    gap: '16px',
    borderRadius: '1000px',
    background: '#E7EFF5',
    color: '#063142',
    textTransform: 'uppercase',
    fontWeight: 500,
    fontSize: '12px',
    boxShadow: 'none',
    '&:hover': {
      background: '#D6E4EF',
      boxShadow: 'none',
    }
  };

  return (
    <PreferencesContainer data-testid="profile-preferences-container">
      <Box sx={{ width: '100%', mb: 3 }} data-testid="profile-preferences-content">
        <CustomTabs data-testid="profile-preferences-tabs">
          <CustomTab 
            icon={<Person />}
            label="Personal Info"
            isActive={activeTab === 0}
            onClick={() => handleTabChange(0)}
            data-testid="profile-preferences-tab-personal"
          />
        
        </CustomTabs>
        
        {activeTab === 0 && (
          <FormContainer data-testid="profile-preferences-personal-form">
            <Stack spacing={3} sx={{ maxWidth: 400, mt: 3 }} data-testid="profile-preferences-personal-stack">
              <Box data-testid="profile-preferences-username-field">
                <Typography variant="subtitle2" color="textSecondary">
                  Username
                </Typography>
                <Typography variant="body1">
                  {user.samId}
                </Typography>
              </Box>

              <Box data-testid="profile-preferences-email-field">
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {user.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }} data-testid="profile-preferences-actions">

              <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={isChangingPassword}
                  sx={buttonStyle}
                  data-testid="profile-preferences-change-password-button"
                >
                  {isChangingPassword ? <CircularProgress size={16} /> : 'Change Password'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={buttonStyle}
                  data-testid="profile-preferences-save-button"
                >
                  {isSaving ? <CircularProgress size={16} /> : 'Save'}
                </Button>

               

                {saveSuccess && (
                  <Typography variant="caption" color="success.main">
                    Settings saved successfully
                  </Typography>
                )}
              </Box>
            </Stack>
          </FormContainer>
        )}

        {activeTab === 1 && (
          <FormContainer data-testid="profile-preferences-settings-form">
            <Typography variant="h5" gutterBottom data-testid="profile-preferences-settings-title">
              User Preferences
            </Typography>
            
            {/* Add your preferences form fields here */}
            <Typography variant="body1" color="textSecondary" data-testid="profile-preferences-settings-message">
              Preferences settings coming soon...
            </Typography>
          </FormContainer>
        )}
      </Box>
    </PreferencesContainer>
  );
} 